import React from 'react';
import { AlertTriangle, Info, Trash, LogOut, X } from 'lucide-react';

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'danger' | 'warning' | 'info';
  isProcessing?: boolean;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  type = 'danger',
  isProcessing = false
}) => {
  if (!isOpen) return null;

  const typeConfig = {
    danger: {
      icon: <AlertTriangle className="text-red-500" size={24} />,
      confirmBtnClass: 'bg-red-600 hover:bg-red-700 text-white',
      bgColor: 'bg-red-50',
      titleColor: 'text-red-800'
    },
    warning: {
      icon: <AlertTriangle className="text-amber-500" size={24} />,
      confirmBtnClass: 'bg-amber-600 hover:bg-amber-700 text-white',
      bgColor: 'bg-amber-50',
      titleColor: 'text-amber-800'
    },
    info: {
      icon: <Info className="text-blue-500" size={24} />,
      confirmBtnClass: 'bg-blue-600 hover:bg-blue-700 text-white',
      bgColor: 'bg-blue-50',
      titleColor: 'text-blue-800'
    }
  };

  const { icon, confirmBtnClass, bgColor, titleColor } = typeConfig[type];

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isProcessing) {
      onCancel();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-scale-in" onClick={e => e.stopPropagation()}>
        <div className={`${bgColor} p-6 rounded-t-xl`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {icon}
            </div>
            <div className="ml-3 flex-1">
              <h3 className={`text-lg font-medium ${titleColor}`}>{title}</h3>
            </div>
            {!isProcessing && (
              <button
                onClick={onCancel}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <X size={20} />
              </button>
            )}
          </div>
        </div>
        
        <div className="p-6">
          <p className="text-gray-600 mb-6">{message}</p>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={isProcessing}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className={`px-4 py-2 rounded-lg transition-colors ${confirmBtnClass}`}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                confirmLabel
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Dialog
export const DeleteConfirmationDialog: React.FC<{
  isOpen: boolean;
  itemName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isProcessing?: boolean;
}> = ({ isOpen, itemName, onConfirm, onCancel, isProcessing = false }) => {
  return (
    <ConfirmationDialog
      isOpen={isOpen}
      title={`Delete ${itemName}`}
      message={`Are you sure you want to delete this ${itemName}? This action cannot be undone.`}
      confirmLabel={`Delete ${itemName}`}
      cancelLabel="Cancel"
      onConfirm={onConfirm}
      onCancel={onCancel}
      type="danger"
      isProcessing={isProcessing}
    />
  );
};

// Logout Confirmation Dialog
export const LogoutConfirmationDialog: React.FC<{
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isProcessing?: boolean;
}> = ({ isOpen, onConfirm, onCancel, isProcessing = false }) => {
  return (
    <ConfirmationDialog
      isOpen={isOpen}
      title="Sign Out"
      message="Are you sure you want to sign out of your account?"
      confirmLabel="Sign Out"
      cancelLabel="Cancel"
      onConfirm={onConfirm}
      onCancel={onCancel}
      type="warning"
      isProcessing={isProcessing}
    />
  );
};

// Discard Changes Confirmation Dialog
export const DiscardChangesDialog: React.FC<{
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ isOpen, onConfirm, onCancel }) => {
  return (
    <ConfirmationDialog
      isOpen={isOpen}
      title="Discard Changes"
      message="You have unsaved changes. Are you sure you want to discard them?"
      confirmLabel="Discard Changes"
      cancelLabel="Keep Editing"
      onConfirm={onConfirm}
      onCancel={onCancel}
      type="warning"
    />
  );
};

// Action Confirmation Dialog
export const ActionConfirmationDialog: React.FC<{
  isOpen: boolean;
  title: string;
  message: string;
  actionLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'danger' | 'warning' | 'info';
  isProcessing?: boolean;
}> = ({ 
  isOpen, 
  title, 
  message, 
  actionLabel, 
  onConfirm, 
  onCancel, 
  type = 'info',
  isProcessing = false
}) => {
  return (
    <ConfirmationDialog
      isOpen={isOpen}
      title={title}
      message={message}
      confirmLabel={actionLabel}
      cancelLabel="Cancel"
      onConfirm={onConfirm}
      onCancel={onCancel}
      type={type}
      isProcessing={isProcessing}
    />
  );
};